// src/components/InteractiveFigure.tsx
'use client';

import * as React from 'react';

export type LabelConfig = {
  id: string;
  label: string;
  description?: string;
};

type AnchorPosition = { x: number; y: number; radius: number };

type InteractiveFigureProps = {
  FigureComponent: React.ComponentType<React.SVGProps<SVGSVGElement> & { ref?: React.Ref<SVGSVGElement> }>;
  labels: LabelConfig[];
  className?: string;
  preserveAspectRatio?: string;
  minWidth?: number;
  centerOffset?: number;
};

export default function InteractiveFigure({
  FigureComponent,
  labels,
  className = '',
  preserveAspectRatio = 'xMidYMid slice',
  minWidth,
  centerOffset = 0,
}: InteractiveFigureProps) {
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const scrollRef = React.useRef<HTMLDivElement | null>(null);
  const svgRef = React.useRef<SVGSVGElement | null>(null);
  const [anchorPositions, setAnchorPositions] = React.useState<
    Record<string, AnchorPosition>
  >({});
  const [activeId, setActiveId] = React.useState<string | null>(null);
  const [isTouch, setIsTouch] = React.useState(false);
  const [figureWidth, setFigureWidth] = React.useState(0);

  // Detect touch / coarse pointer devices (mobile / tablet)
  React.useEffect(() => {
    if (typeof window === 'undefined') return;

    const mq = window.matchMedia('(pointer: coarse)');
    const update = () => setIsTouch(mq.matches);
    update();

    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  const recomputeAnchors = React.useCallback(() => {
    if (!containerRef.current || !svgRef.current || !scrollRef.current) return;

    const containerBox = containerRef.current.getBoundingClientRect();
    const positions: Record<string, AnchorPosition> = {};

    labels.forEach(({ id }) => {
      const el = svgRef.current!.querySelector<SVGCircleElement>(`#${id}`);
      if (!el) return;

      // Use SVG coordinate system for accurate positioning independent of scroll state
      try {
        const svgBBox = el.getBBox();
        const svgMatrix = svgRef.current!.getScreenCTM();

        if (svgMatrix) {
          // Convert SVG coordinates to screen coordinates using the SVG's transform matrix
          const svgPoint = svgRef.current!.createSVGPoint();
          svgPoint.x = svgBBox.x + svgBBox.width / 2;
          svgPoint.y = svgBBox.y + svgBBox.height / 2;

          const screenPoint = svgPoint.matrixTransform(svgMatrix);

          // Convert to container-relative coordinates
          const x = screenPoint.x - containerBox.left;
          const y = screenPoint.y - containerBox.top;

          // Use getBoundingClientRect for radius (it's simpler and accurate enough)
          const box = el.getBoundingClientRect();
          const radius = Math.max(box.width, box.height) / 2;

          positions[id] = { x, y, radius };
          return;
        }
      } catch {
        // Fall through to getBoundingClientRect fallback
      }

      // Fallback to getBoundingClientRect if SVG coordinate transform fails
      const box = el.getBoundingClientRect();
      const x = box.left + box.width / 2 - containerBox.left;
      const y = box.top + box.height / 2 - containerBox.top;
      const radius = Math.max(box.width, box.height) / 2;

      positions[id] = { x, y, radius };
    });

    setAnchorPositions(positions);
    setFigureWidth(containerBox.width);
  }, [labels]);

  React.useLayoutEffect(() => {
    recomputeAnchors();
    window.addEventListener('resize', recomputeAnchors);
    return () => window.removeEventListener('resize', recomputeAnchors);
  }, [recomputeAnchors]);

  const centerScrollViewport = React.useCallback(() => {
    const scroller = scrollRef.current;
    if (!scroller) return;

    const scrollWidth = scroller.scrollWidth;
    const viewportWidth = scroller.clientWidth;
    const extraSpace = scrollWidth - viewportWidth;

    if (extraSpace <= 0) {
      scroller.scrollLeft = 0;
      // Recompute anchors after resetting scroll
      requestAnimationFrame(() => recomputeAnchors());
      return;
    }

    const desiredCenter = scrollWidth / 2 + centerOffset;
    const desiredLeft = desiredCenter - viewportWidth / 2;
    const clampedLeft = Math.max(0, Math.min(desiredLeft, extraSpace));
    scroller.scrollLeft = clampedLeft;
    // Recompute anchors after scrolling
    requestAnimationFrame(() => recomputeAnchors());
  }, [centerOffset, recomputeAnchors]);

  React.useLayoutEffect(() => {
    centerScrollViewport();
  }, [centerScrollViewport]);

  React.useEffect(() => {
    window.addEventListener('resize', centerScrollViewport);
    return () => window.removeEventListener('resize', centerScrollViewport);
  }, [centerScrollViewport]);

  // Recompute anchors when scroll position changes
  React.useEffect(() => {
    const scroller = scrollRef.current;
    if (!scroller) return;

    const handleScroll = () => {
      recomputeAnchors();
    };

    scroller.addEventListener('scroll', handleScroll, { passive: true });
    return () => scroller.removeEventListener('scroll', handleScroll);
  }, [recomputeAnchors]);

  const [containerWidth, setContainerWidth] = React.useState(0);

  React.useEffect(() => {
    if (!scrollRef.current) return;
    const el = scrollRef.current;
    const update = () => {
      setContainerWidth(el.clientWidth);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  return (
    <div ref={rootRef} className={`absolute inset-0 h-full w-full ${className}`}>
      <div
        ref={scrollRef}
        className="h-full w-full overflow-x-auto overflow-y-hidden"
      >
        <div
          ref={containerRef}
          className="relative h-full flex-shrink-0"
        >
          <FigureComponent
            ref={svgRef}
            className="block h-full max-h-full pointer-events-none select-none"
            style={{
              width: minWidth ? Math.max(containerWidth, minWidth) : '100%',
            }}
            preserveAspectRatio={preserveAspectRatio}
          />

          <div className="pointer-events-none absolute inset-0 z-20">
            {labels.map(({ id, label, description }) => {
              const pos = anchorPositions[id];
              if (!pos) return null;

              const isActive = activeId === id;
              const shouldFlip = figureWidth > 0 && pos.x > figureWidth / 2;

              return (
                <div
                  key={id}
                  className="absolute pointer-events-auto"
                  style={{
                    left: pos.x - pos.radius,
                    top: pos.y - pos.radius,
                  }}
                >
                  <Callout
                    id={id}
                    label={label}
                    description={description}
                    isTouch={isTouch}
                    isActive={isActive}
                    radius={pos.radius}
                    alignment={shouldFlip ? 'left' : 'right'}
                    onActivate={() => {
                      if (!isTouch) return;
                      setActiveId(prev => (prev === id ? null : id));
                    }}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

type CalloutProps = {
  id: string;
  label: string;
  description?: string;
  isTouch: boolean;
  isActive: boolean;
  radius: number;
  alignment: 'left' | 'right';
  onActivate: () => void;
};

function Callout({ label, description, isTouch, isActive, radius, alignment, onActivate }: CalloutProps) {
  const [isHovered, setIsHovered] = React.useState(false);

  const showPopup = isTouch ? isActive : isHovered;
  const diameter = radius * 2;
  const tooltipOffset = diameter / 2 + 8;
  const hiddenTranslateClass = alignment === 'left' ? 'translate-x-2' : '-translate-x-2';
  const tooltipPositionStyle =
    alignment === 'left'
      ? { right: `${tooltipOffset}px` }
      : { left: `${tooltipOffset}px` };

  const handleMouseEnter = () => {
    if (isTouch) return;
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    if (isTouch) return;
    setIsHovered(false);
  };

  return (
    <div
      className="relative flex items-center"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Interactive circle matching the actual circle size */}
      <button
        type="button"
        className={`relative flex items-center justify-center rounded-full transition-all duration-300 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-300 focus-visible:ring-offset-2 ${isTouch ? 'cursor-pointer' : 'cursor-pointer'
          }`}
        style={{
          width: diameter,
          height: diameter,
        }}
        aria-label={label}
        aria-expanded={showPopup}
        onClick={isTouch ? onActivate : undefined}
      >
        {/* Subtle always-visible indicator */}
        <span
          className={`absolute z-20 inset-0 rounded-full border border-white/40 shadow-[0_0_0_1px_rgba(15,23,42,0.18)] transition-all duration-300 ease-out ${showPopup || isHovered
              ? 'bg-white/10 scale-110 shadow-[0_0_0_1px_rgba(15,23,42,0.32)]'
              : 'opacity-70'
            }`}
        />
      </button>

      {/* Label / popup */}
      <div
        className={`absolute z-30 transition-all duration-200 ease-out ${showPopup
            ? 'opacity-100 translate-x-0 pointer-events-auto'
            : `opacity-0 ${hiddenTranslateClass} pointer-events-none`
          }`}
        style={tooltipPositionStyle}
      >
        <div className="relative">
          {/* Tooltip panel */}
          <div className="rounded-xl bg-white/95 backdrop-blur-md border border-slate-200/80 shadow-xl px-3.5 py-2.5 min-w-[160px]">
            <p className="text-xs font-semibold text-slate-900 whitespace-nowrap">
              {label}
            </p>
            {description && (
              <p className="mt-1 text-[11px] leading-snug text-slate-600">
                {description}
              </p>
            )}
          </div>

          {/* Little arrow */}
          {alignment === 'left' ? (
            <div className="absolute -right-1 top-1/2 -translate-y-1/2">
              <div
                className="
                  w-0 h-0
                  border-y-5
                  border-y-transparent
                  border-l-5
                  border-l-white/95
                "
              />
            </div>
          ) : (
            <div className="absolute -left-1 top-1/2 -translate-y-1/2">
              <div
                className="
                  w-0 h-0
                  border-y-5
                  border-y-transparent
                  border-r-5
                  border-r-white/95
                "
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
